import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Shield, 
  Code, 
  ArrowRight, 
  CheckCircle, 
  Star,
  Users,
  FileText
} from 'lucide-react'
import { 
  services, 
  caseStudies, 
  blogPosts, 
  testimonials, 
  stats, 
  timelineSteps
} from '../lib/data'

const HomePage = () => {
  const developmentServices = services.filter(s => s.category === 'development').slice(0, 5)
  const securityServices = services.filter(s => s.category === 'security').slice(0, 5)
  const featuredCaseStudies = caseStudies.slice(0, 3) // Show latest 3 case studies
  const featuredInsights = blogPosts.slice(0, 3) // Show latest 3 insights

  return (
    <>
      <Helmet>
        <title>DevSecRoot - dev = development. sec = security. root = fundamentals.</title>
        <meta name="description" content="We engineer products where security is a first-class feature—architected at the root, not bolted on. Book your security assessment today." />
        <meta name="keywords" content="devsec, security, development, penetration testing, web application security, API security, full-stack development" />
        <link rel="canonical" href="https://devsecroot.com" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 grid-pattern opacity-20" />
        
        {/* Particles */}
        <div className="particles">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 6}s`,
                animationDuration: `${6 + Math.random() * 4}s`
              }}
            />
          ))}
        </div>

        <div className="container-max text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-dsr-text mb-6">
              Secure Development,
              <br />
              <span className="text-gradient terminal-cursor">Done Right</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-dsr-text/80 mb-8 max-w-3xl mx-auto">
              We engineer products where security is a first-class feature—architected at the root, not bolted on.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Book a Security Assessment
              </Link>
              <Link to="/contact" className="btn-secondary text-lg px-8 py-4">
                Talk to an Architect
              </Link>
            </div>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-16"
          >
            <div className="flex flex-wrap justify-center items-center gap-8 text-dsr-text/60">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-dsr-accent" />
                <span className="text-sm font-medium">OWASP Partner</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-dsr-accent" />
                <span className="text-sm font-medium">ISO 27001 Ready</span>
              </div>
              <div className="flex items-center space-x-2">
                <Star className="w-5 h-5 text-dsr-accent" />
                <span className="text-sm font-medium">98% Client Satisfaction</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              What We Do
            </h2>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Comprehensive security and development services to protect and scale your business
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Security Services */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card-hover p-8"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-dsr-text">Security Services</h3>
                  <p className="text-dsr-text/70">Protect your applications and infrastructure</p>
                </div>
              </div>
              
              <ul className="space-y-3 mb-6">
                {securityServices.map((service) => (
                  <li key={service.id} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-dsr-accent flex-shrink-0" />
                    <span className="text-dsr-text/80">{service.title}</span>
                  </li>
                ))}
              </ul>
              
              <Link 
                to="/security" 
                className="inline-flex items-center space-x-2 text-dsr-accent hover:text-cyan-400 transition-colors"
              >
                <span>Learn more about security</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            {/* Development Services */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card-hover p-8"
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <Code className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-dsr-text">Development Services</h3>
                  <p className="text-dsr-text/70">Build scalable, secure applications</p>
                </div>
              </div>
              
              <ul className="space-y-3 mb-6">
                {developmentServices.map((service) => (
                  <li key={service.id} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-dsr-accent flex-shrink-0" />
                    <span className="text-dsr-text/80">{service.title}</span>
                  </li>
                ))}
              </ul>
              
              <Link 
                to="/development" 
                className="inline-flex items-center space-x-2 text-dsr-accent hover:text-cyan-400 transition-colors"
              >
                <span>Learn more about development</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="section-padding bg-dsr-card/30">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Featured Services
            </h2>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Core services that form the foundation of our security and development expertise
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-card-hover p-6"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-black" />
                </div>
                <h3 className="text-xl font-semibold text-dsr-text mb-3">{service.title}</h3>
                <p className="text-dsr-text/70 mb-4">{service.description}</p>
                <Link 
                  to={service.link}
                  className="inline-flex items-center space-x-2 text-dsr-accent hover:text-cyan-400 transition-colors text-sm"
                >
                  <span>Learn more</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof/Outcomes Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Proven Results
            </h2>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Numbers that speak to our expertise and commitment to excellence
            </p>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-gradient mb-2">
                  {stat.value}
                </div>
                <div className="text-lg font-medium text-dsr-text mb-1">
                  {stat.label}
                </div>
                <div className="text-sm text-dsr-text/70">
                  {stat.description}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Testimonial */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-card-hover p-8 max-w-4xl mx-auto"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-full flex items-center justify-center">
                <Users className="w-8 h-8 text-black" />
              </div>
              <div>
                <h4 className="text-xl font-semibold text-dsr-text">{testimonials[0].name}</h4>
                <p className="text-dsr-text/70">{testimonials[0].role} at {testimonials[0].company}</p>
              </div>
            </div>
            <blockquote className="text-lg text-dsr-text/80 italic">
              "{testimonials[0].content}"
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-dsr-card/30">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Our Process
            </h2>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              A systematic approach to delivering secure, scalable solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {timelineSteps.map((step, index) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center relative"
              >
                {index < timelineSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-dsr-accent to-transparent" />
                )}
                
                <div className="w-16 h-16 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-black" />
                </div>
                
                <h3 className="text-xl font-semibold text-dsr-text mb-3">{step.title}</h3>
                <p className="text-dsr-text/70">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="section-padding">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Featured Case Studies
            </h2>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Real-world examples of how we've helped clients achieve their security and development goals
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {featuredCaseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-card-hover overflow-hidden"
              >
                <div className="h-48 bg-gradient-to-br from-dsr-accent/20 to-cyan-500/20 flex items-center justify-center">
                  <Shield className="w-16 h-16 text-dsr-accent" />
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      study.category === 'security' 
                        ? 'bg-red-500/20 text-red-400' 
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {study.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-dsr-text mb-3">{study.title}</h3>
                  <p className="text-dsr-text/70 mb-4">{study.description}</p>
                  <Link 
                    to={`/case-studies/${study.slug}`}
                    className="inline-flex items-center space-x-2 text-dsr-accent hover:text-cyan-400 transition-colors"
                  >
                    <span>Read case study</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Insights Section */}
      <section className="section-padding bg-dsr-card/30">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Featured Insights
            </h2>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Stay ahead with our latest security and development insights
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {featuredInsights.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-card-hover overflow-hidden"
              >
                <div className="h-48 bg-gradient-to-br from-dsr-accent/20 to-cyan-500/20 flex items-center justify-center">
                  <FileText className="w-16 h-16 text-dsr-accent" />
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-2 mb-3">
                    {post.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full text-xs font-medium bg-dsr-accent/20 text-dsr-accent">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-xl font-semibold text-dsr-text mb-3">{post.title}</h3>
                  <p className="text-dsr-text/70 mb-4">{post.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-dsr-text/60">{post.readingTime} min read</span>
                    <Link 
                      to={`/insights/${post.slug}`}
                      className="inline-flex items-center space-x-2 text-dsr-accent hover:text-cyan-400 transition-colors"
                    >
                      <span>Read more</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section-padding">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Ship Fast. Ship Secure.
            </h2>
            <p className="text-xl text-dsr-text/70 mb-8 max-w-2xl mx-auto">
              Ready to build secure, scalable applications? Let's discuss your project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Start Your Project
              </Link>
              <Link to="/contact" className="btn-secondary text-lg px-8 py-4">
                Schedule a Call
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default HomePage
